import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, MessageCircle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getAirline } from '../../utils/airlines';
import { getCityName } from '../../utils/airports';
import { FlightStats } from './FlightStats';
import { ChatView } from '../chat/ChatView';
import { AvatarImg } from '../shared/Avatar';

interface FriendProfileSheetProps {
  friendId: string;
  onClose: () => void;
  onMessage?: (friendId: string) => void;
}

export function FriendProfileSheet({ friendId, onClose, onMessage }: FriendProfileSheetProps) {
  const { usersById, allFlights, friends } = useApp();
  const [chatOpen, setChatOpen] = useState(false);

  const friend = usersById.get(friendId);
  const airline = friend ? getAirline(friend.airlineCode) : null;

  const friendFlights = useMemo(
    () => allFlights.filter((f) => f.userId === friendId),
    [allFlights, friendId],
  );

  const portalTarget = document.getElementById('app-shell') || document.body;

  if (!friend || !airline) return null;

  return createPortal(
    <div className="absolute inset-0 z-[100] flex flex-col bg-slate-900">
      {/* Header */}
      <div className="shrink-0 bg-slate-800 border-b border-slate-700/50" style={{ paddingTop: 'var(--sat)' }}>
        <div className="flex items-center gap-3 px-3 py-2.5">
          <button
            onClick={onClose}
            className="p-1.5 -ml-1 rounded-lg hover:bg-slate-700/50 active:bg-slate-600/50 transition-colors"
          >
            <ArrowLeft size={22} className="text-slate-200" />
          </button>
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 overflow-hidden"
            style={{ backgroundColor: airline.color, color: airline.textOnColor }}
          >
            <AvatarImg src={friend.avatarUrl} name={friend.name} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-semibold text-slate-100 truncate">{friend.name}</div>
            <div className="text-xs text-slate-500">@{friend.handle}</div>
          </div>
          <button
            onClick={() => onMessage ? onMessage(friendId) : setChatOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent text-white text-xs font-semibold active:scale-95 transition-transform"
          >
            <MessageCircle size={14} />
            Message
          </button>
        </div>
      </div>

      {/* Info bar */}
      <div className="shrink-0 px-4 py-2.5 bg-slate-800/60 border-b border-slate-700/30 flex items-center gap-3">
        <span
          className="px-2 py-0.5 rounded-md text-[11px] font-bold"
          style={{ backgroundColor: airline.color + '20', color: airline.color }}
        >
          {airline.name}
        </span>
        <span className="text-xs text-slate-400">
          Base: <span className="font-mono text-slate-300">{friend.baseAirport}</span>
          {' '}
          <span className="text-slate-500">({getCityName(friend.baseAirport)})</span>
        </span>
        {friend.instagramHandle && (
          <button
            onClick={() => window.open(`https://instagram.com/${friend.instagramHandle}`, '_blank')}
            className="ml-auto flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200 transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
            </svg>
            @{friend.instagramHandle}
          </button>
        )}
      </div>

      {/* Content — scrollable */}
      <div className="flex-1 overflow-y-auto pb-8">
        <FlightStats
          flights={friendFlights}
          user={friend}
          friendCount={friends.length}
        />
      </div>

      {/* Internal chat — stacks on top of profile */}
      {chatOpen && (
        <ChatView
          partnerId={friendId}
          onClose={() => setChatOpen(false)}
        />
      )}
    </div>,
    portalTarget,
  );
}
